<template>
  <b-card no-body :img-src="productImage" :img-alt="product.name" img-top>
    <b-card-title
      class="flex-1 px-2 py-2 m-0 d-flex justify-content-between border-top"
    >
      <span>
        <small>{{ product.name }}</small>
        <span class="d-flex flex-wrap">
          {{ product.price | formatCurrency }}
        </span>
      </span>
    </b-card-title>

    <b-card-text v-if="!disabled">
      <div class="px-2 mt-1">
        <b-input-group>
          <b-input-group-prepend>
            <b-button
              :disabled="quantity === 1"
              class="rounded-0"
              @click="decrement"
            >
              <span class="fas fa-minus" />
            </b-button>
          </b-input-group-prepend>
          <b-input v-model.number="quantity" type="number" min="1" />
          <b-input-group-append>
            <b-button class="rounded-0" @click="increment">
              <span class="fas fa-plus" />
            </b-button>
          </b-input-group-append>
        </b-input-group>

        <!-- Pagamento único + MULTI -->
        <div class="d-flex flex-wrap justify-content-stretch gap-1 my-2">
          <b-button
            variant="success"
            class="flex-1"
            @click="onTriggerSingle(PAYMENT_METHODS_IDS.CASH)"
            >DI</b-button
          >
          <b-button
            variant="warning"
            class="flex-1"
            @click="onTriggerSingle(PAYMENT_METHODS_IDS.CREDIT_CARD)"
            >CC</b-button
          >
          <b-button
            variant="info"
            class="flex-1"
            @click="onTriggerSingle(PAYMENT_METHODS_IDS.DEBIT_CARD)"
            >CD</b-button
          >
          <b-button
            variant="secondary"
            class="flex-1"
            @click="onTriggerSingle(PAYMENT_METHODS_IDS.PIX)"
            >PIX</b-button
          >
          <b-button
            variant="outline-info"
            class="flex-1"
            title="Pagar com múltiplos métodos"
            @click="openMulti"
          >
            <span class="fas fa-plus-circle" />
          </b-button>
        </div>
      </div>
    </b-card-text>

    <!-- Modal MULTI -->
    <b-modal
      v-model="multiOpen"
      :title="`Pagamento Múltiplo — ${product.name}`"
      ok-title="Confirmar"
      cancel-title="Cancelar"
      @ok="confirmMulti"
    >
      <!-- Topo com total -->
      <div class="mb-3 d-flex align-items-center justify-content-between">
        <div class="text-muted">Total</div>
        <div class="h5 m-0">{{ totalAmount | formatCurrency }}</div>
      </div>
      <hr class="my-2" />

      <!-- Inputs com prefixo R$ -->
      <b-form-group label="Dinheiro (DI)" class="mb-2">
        <b-input-group>
          <b-input-group-prepend is-text>R$</b-input-group-prepend>
          <b-input
            v-model.number="multi.CASH"
            type="number"
            min="0"
            step="0.01"
            @input="onMultiChange"
          />
        </b-input-group>
      </b-form-group>

      <b-form-group label="Crédito (CC)" class="mb-2">
        <b-input-group>
          <b-input-group-prepend is-text>R$</b-input-group-prepend>
          <b-input
            v-model.number="multi.CREDIT_CARD"
            type="number"
            min="0"
            step="0.01"
            @input="onMultiChange"
          />
        </b-input-group>
      </b-form-group>

      <b-form-group label="Débito (CD)" class="mb-2">
        <b-input-group>
          <b-input-group-prepend is-text>R$</b-input-group-prepend>
          <b-input
            v-model.number="multi.DEBIT_CARD"
            type="number"
            min="0"
            step="0.01"
            @input="onMultiChange"
          />
        </b-input-group>
      </b-form-group>

      <b-form-group label="PIX" class="mb-3">
        <b-input-group>
          <b-input-group-prepend is-text>R$</b-input-group-prepend>
          <b-input
            v-model.number="multi.PIX"
            type="number"
            min="0"
            step="0.01"
            @input="onMultiChange"
          />
        </b-input-group>
      </b-form-group>

      <!-- Mensagem dinâmica -->
      <b-alert
        v-if="showMsg"
        :variant="isUnder ? 'warning' : 'danger'"
        show
        class="py-2"
      >
        <template v-if="isUnder">
          Faltam <strong>{{ remaining | formatCurrency }}</strong> para fechar o
          total.
        </template>
        <template v-else>
          Excedeu em <strong>{{ Math.abs(remaining) | formatCurrency }}</strong
          >. Ajuste os valores.
        </template>
      </b-alert>

      <small v-else class="text-success d-block mt-1">
        ✔ Total conferido.
      </small>
    </b-modal>
  </b-card>
</template>

<script>
import {
  PAYMENT_METHODS_IDS,
  PRODUCT_ACTIONS,
  PRODUCT_DEFAULT_IMAGE
} from '@/constants'
import { formatCurrency } from '@/helpers/numbers'

export default {
  name: 'ProductListItem',
  filters: { formatCurrency },
  props: {
    product: { type: Object, required: true },
    disabled: { type: Boolean }
  },
  data() {
    return {
      quantity: 1,
      multiOpen: false,
      touchedMulti: false, // controla exibição da mensagem
      multi: {
        CASH: 0,
        CREDIT_CARD: 0,
        DEBIT_CARD: 0,
        PIX: 0
      }
    }
  },
  computed: {
    productImage() {
      return this.product.image || PRODUCT_DEFAULT_IMAGE
    },
    unitPrice() {
      return Number(this.product.price || 0)
    },
    totalAmount() {
      return this.unitPrice * this.quantity
    },
    sumMulti() {
      const toNum = (v) => (isNaN(v) || v == null ? 0 : Number(v))
      return (
        toNum(this.multi.CASH) +
        toNum(this.multi.CREDIT_CARD) +
        toNum(this.multi.DEBIT_CARD) +
        toNum(this.multi.PIX)
      )
    },
    remaining() {
      // total - soma; negativo => excedeu
      return Number((this.totalAmount - this.sumMulti).toFixed(2))
    },
    isExact() {
      return Math.abs(this.remaining) < 0.01
    },
    isUnder() {
      return this.remaining > 0.01 // ainda falta
    },
    isOver() {
      return this.remaining < -0.01 // excedeu
    },
    showMsg() {
      return !this.isExact
    },
    // Mostra a mensagem de “faltam X” somente se usuário já mexeu e NÃO bateu o total
    showRemainingMsg() {
      // Sempre mostra se não bate o total
      return Math.abs(this.remaining) >= 0.01
    },
    isMultiValid() {
      return this.totalAmount > 0 && Math.abs(this.remaining) < 0.01
    }
  },
  created() {
    this.PRODUCT_ACTIONS = PRODUCT_ACTIONS
    this.PAYMENT_METHODS_IDS = PAYMENT_METHODS_IDS
  },
  methods: {
    // Pagamento único
    onTriggerSingle(paymentMethod) {
      const { product, quantity } = this
      const data = { quantity, paymentMethod }
      const action = PRODUCT_ACTIONS.BUY
      this.$emit('trigger-action', { action, product, data })
      this.quantity = 1
    },

    // Abrir MULTI
    openMulti() {
      this.multi = { CASH: 0, CREDIT_CARD: 0, DEBIT_CARD: 0, PIX: 0 }
      this.touchedMulti = false
      this.multiOpen = true
    },

    // Cada alteração nos inputs marca como "tocado" e atualiza a mensagem
    onMultiChange() {
      if (!this.touchedMulti) this.touchedMulti = true
    },

    // Confirmar MULTI (não desabilita botão; valida aqui)
    confirmMulti() {
      if (!this.isMultiValid) {
        this.touchedMulti = true
        // Apenas uma proteção extra além da mensagem visual
        this.$root.$toast?.error?.(
          'A soma das formas de pagamento deve ser igual ao total.'
        )
        return false // impede fechar o modal
      }

      const { product, quantity, PAYMENT_METHODS_IDS } = this
      const payments = []
      const pushIf = (typeKey, amount) => {
        const val = Number(amount || 0)
        if (val > 0)
          payments.push({ type: PAYMENT_METHODS_IDS[typeKey], amount: val })
      }

      pushIf('CASH', this.multi.CASH)
      pushIf('CREDIT_CARD', this.multi.CREDIT_CARD)
      pushIf('DEBIT_CARD', this.multi.DEBIT_CARD)
      pushIf('PIX', this.multi.PIX)

      const data = { quantity, payments }
      const action = PRODUCT_ACTIONS.BUY
      this.$emit('trigger-action', { action, product, data })

      this.quantity = 1
      this.multiOpen = false
    },

    increment() {
      this.quantity++
    },
    decrement() {
      this.quantity--
    }
  }
}
</script>

<style lang="scss" scoped>
.card-footer {
  padding: 0px;
}
.actions {
  column-count: 4;
}
.card-title {
  font-size: 1rem;
  font-weight: bold;
}
.display-table {
  display: table;
}
.display-table > div {
  display: table-row;
}
.display-table > div > div {
  display: table-cell;
}
.card {
  width: 100%;
  max-width: none;
  height: 100%;
}
.btn {
  font-size: 13px;
}
</style>
